// @flow
import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import ReactPlayer from 'react-player/file';
import Cropper from 'react-cropper';
import { useTranslation } from 'react-i18next';
import { Line } from 'rc-progress';
import noImageLoadedIcon from 'assets/images/dropimages-icon.svg';
import imgTurnLeft from 'assets/images/rotate_left.png';
import imgTurnRight from 'assets/images/rotate_right.png';
import imgDelete from 'assets/images/delete.png';
import supportService from 'api/support';
import 'cropperjs/dist/cropper.css';
import imgCrop from 'assets/images/crop.png';

import {
  PageContainer,
  Container,
  FileUploadContainer,
  UploadImage,
  SmallTitle,
  OptionsContainer,
  Option,
  UploadInProgressContainer,
} from './Styles';

type GoalcardUploadOptionProps = {
  setUploadedMediaCallback(uploadedMedia: string): {},
};

const GoalcardUploadOption = (props: GoalcardUploadOptionProps) => {
  const { t } = useTranslation();
  const { setUploadedMediaCallback } = props;
  const [cropper, setCropper] = useState<any>();
  const [uploadedMedia, setUploadedMedia]: [any, ((any => any) | any) => void] = useState({});
  const [uploadProgress, setuploadProgress]: [any, ((any => any) | any) => void] = useState(0);

  console.log('PROPS IMAGE', props.uploadedMedia);
  console.log('STATE IMAGE', uploadedMedia);

  const removeMedia = () => {
    setUploadedMedia({
      base64Image: '',
      videoUrl: '',
      uploadInProgress: false,
    });

    setUploadedMediaCallback({});
  };

  useEffect(() => {
    removeMedia();

    if (props.uploadedMedia) {
      setUploadedMedia({ ...props.uploadedMedia });
    }
  }, []);

  const rotate64 = (base64data, degrees) => new Promise(((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.setAttribute('id', 'hidden-canvas');
    canvas.style.display = 'none';
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    const image = new Image();
    // assume png if not provided
    image.src = (base64data.indexOf(',') === -1 ? 'data:image/png;base64,' : '') + base64data;
    image.onload = () => {
      const w = image.width;
      const h = image.height;
      const rads = (degrees * Math.PI) / 180;
      let c = Math.cos(rads);
      let s = Math.sin(rads);
      if (s < 0) { s = -s; }
      if (c < 0) { c = -c; }
      // use translated width and height for new canvas
      canvas.width = h * s + w * c;
      canvas.height = h * c + w * s;
      // draw the rect in the center of the newly sized canvas
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);
      // assume plain base64 if not provided

      const canvasDataURL = canvas.toDataURL();
      resolve(canvasDataURL);

      document.body.removeChild(canvas);
    };
    image.onerror = (e) => { reject(new Error('Unable to rotate Image', e)); };
  }));

  const resizeImage = (image, trgHeight, trgWidth, srcWidth, srcHeight, callback) => {
    const canvas = document.createElement('canvas');
    canvas.width = trgWidth;
    canvas.height = trgHeight;

    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');

      ctx.drawImage(img, 0, 0, srcWidth, srcHeight, 0, 0, trgWidth, trgHeight);
      ctx.fill();

      const dataUrlResized = canvas.toDataURL();

      callback(dataUrlResized);
    };

    img.src = image;
  };

  const cropData = () => {
    if (typeof cropper !== 'undefined') {
      const base64Image = cropper.getCroppedCanvas().toDataURL();

      setUploadedMedia({ base64Image });
      setUploadedMediaCallback({ base64Image });
    }
  };

  const rotateRight = () => {
    if (typeof cropper !== 'undefined') {
      cropper.rotate(90);

      rotate64(uploadedMedia.base64Image, 90).then((result) => {
        setUploadedMediaCallback({ base64Image: result });
      });
    }
  };

  const rotateLeft = () => {
    if (typeof cropper !== 'undefined') {
      cropper.rotate(-90);

      rotate64(uploadedMedia.base64Image, -90).then((result) => {
        setUploadedMediaCallback({ base64Image: result });
      });
    }
  };

  const progressUpdate = (progress) => {
    setuploadProgress(progress);
  };

  const onDropAcceptedHandler = (acceptedFiles) => {
    const file = acceptedFiles[0];

    const { type } = file;
    if (type.includes('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        let base64Image = event.target.result;
        const image = new Image();
        image.onload = () => {
          if (file.type !== 'image/gif') {
            const MAX_HEIGHT = 300;
            const MAX_WIDTH = 300;
            let { width, height } = image;
            if (width >= MAX_WIDTH || height >= MAX_HEIGHT) {
              if (width > height) {
                if (width > MAX_WIDTH) {
                  height *= MAX_WIDTH / width;
                  width = MAX_WIDTH;
                }
              } else if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }

              resizeImage(base64Image, height, width, width, height, (base64Resized) => {
                base64Image = base64Resized;
              });
            }
          }

          setUploadedMedia({ base64Image });
          setUploadedMediaCallback({ base64Image });
        };

        image.src = base64Image;
      };
      reader.readAsDataURL(file);
    } else {
      setUploadedMedia({
        ...uploadedMedia,
        uploadInProgress: true,
      });

      supportService.postFile(file, progressUpdate)
        .then((videoUrlResponse) => {
          const videoUrl = videoUrlResponse.replace(/"/g, '');
          setUploadedMedia({
            videoUrl,
          });

          setUploadedMediaCallback({ videoUrl });
        });
    }
  };

  const renderDropzone = () => {
    if (uploadedMedia.base64Image || uploadedMedia.videoUrl || uploadedMedia.uploadInProgress) {
      return null;
    }

    return (
      <Dropzone
        multiple={false}
        maxSize={209715200} // 200mb in bytes
        onDropAccepted={(files) => onDropAcceptedHandler(files)}
      >
        {({ getRootProps, getInputProps }) => (
          <FileUploadContainer {...getRootProps()}>
            <input {...getInputProps()} accept="image/jpeg, image/png, image/gif, video/mp4, video/webm, video/quicktime" />
            <UploadImage src={noImageLoadedIcon} alt="upload" />
            <SmallTitle>{t('customize_upload_title')}</SmallTitle>
            <SmallTitle>{t('customize_upload_description')}</SmallTitle>
          </FileUploadContainer>
        )}
      </Dropzone>
    );
  };

  const renderCropper = () => {
    if (!uploadedMedia.base64Image) {
      return null;
    }

    return (
      <>
        <Cropper
          style={{ width: '90%' }}
          src={uploadedMedia.base64Image}
          viewMode={2}
          responsive
          restore
          checkCrossOrigin
          checkOrientation={false}
          modal
          guides
          center
          highlight
          background
          autoCrop
          movable
          rotatable
          scalable
          zoomable
          zoomOnTouch
          zoomOnWheel
          cropBoxMovable
          onInitialized={(instance) => { setCropper(instance); }}
        />
        <OptionsContainer>
          <Option src={imgTurnLeft} onClick={rotateLeft} />
          <Option src={imgTurnRight} onClick={rotateRight} />
          <Option src={imgCrop} onClick={cropData} />
          <Option src={imgDelete} onClick={removeMedia} />
        </OptionsContainer>
      </>
    );
  };

  const renderVideo = () => {
    if (uploadedMedia.uploadInProgress === true) {
      return (
        <UploadInProgressContainer>
          <SmallTitle>{t('customize_upload_inprogress_title')}</SmallTitle>
          <SmallTitle>{t('customize_upload_inprogress_description')}</SmallTitle>
          <Line percent={uploadProgress} strokeWidth="3" strokeColor="#17bfdd" />

        </UploadInProgressContainer>
      );
    }

    if (uploadedMedia.videoUrl) {
      return (
        <>
          <ReactPlayer
            url={uploadedMedia.videoUrl}
            width="90%"
            height="90%"
            controls
          />
          <OptionsContainer>
            <Option src={imgDelete} onClick={removeMedia} />
          </OptionsContainer>
        </>
      );
    }

    return null;
  };

  return (
    <PageContainer>
      <Container>
        {renderDropzone()}
        {renderCropper()}
        {renderVideo()}
      </Container>
    </PageContainer>
  );
};

export default GoalcardUploadOption;
